import { memo, useMemo, useState } from 'react';
import {
  Card,
  Stack,
  Text,
  Title,
  Group,
  Button,
  ThemeIcon,
  Divider,
  Flex,
  Loader,
} from '@mantine/core';
import { Glasses, Plus, Watch } from 'lucide-react';
import { useDisclosure } from '@mantine/hooks';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import type { Device, DeviceDYNCode } from '@types';
import { unpairDeviceSafe } from '../../services/deviceManager';
import ConnectDeviceModal from '../../components/ConnectDevice.modal';
import CreateDeviceModal from '../../components/CreateDevice.modal';
import DeviceActionButtonsGroup from '@components/ui-logic/DeviceActionButtonsGroup';
import { ConnectionBadge } from '@components/ui/ConnectionBadge';
import {
  DeleteDeviceButton,
  DevicePairingActionsButtonGroup,
} from '@components';
import { canPairSensor } from '../../utils/devicePairingLimits';

interface DeviceListProps {
  userId: string;
  devices: Device[];
  deviceTokens: DeviceDYNCode[];
  isLoading: boolean;
  error: string | null;
  hideConnectionBadges?: boolean;
  hideCreateButtons?: boolean;
  onDeviceCreated?: () => void;
  onRefresh: () => void;
  onDeleteDevice: (deviceId: string) => Promise<void>;
}

// Devices Header Component
const DevicesHeader = memo(function DevicesHeader({
  hideCreateButtons,
  onCreateVR,
  onCreateSensor,
}: {
  hideCreateButtons: boolean;
  onCreateVR: () => void;
  onCreateSensor: () => void;
}) {
  return (
    <Group justify='space-between'>
      <Stack gap={2}>
        <Title order={3}>I Tuoi Dispositivi</Title>
        <Text size='sm' c='dimmed'>
          Gestisci visori VR e sensori (ogni visore può avere al massimo 1
          sensore)
        </Text>
      </Stack>
      {!hideCreateButtons && (
        <Group gap='xs'>
          <Button
            onClick={onCreateVR}
            leftSection={<Plus size={16} />}
            variant='light'
            color='blue'
          >
            Aggiungi Visore
          </Button>
          <Button
            onClick={onCreateSensor}
            leftSection={<Plus size={16} />}
            variant='light'
            color='green'
          >
            Aggiungi Sensore
          </Button>
        </Group>
      )}
    </Group>
  );
});

// VR Headset Section Component
const VRHeadsetSection = memo(function VRHeadsetSection({
  device,
  deviceTokens,
  hideConnectionBadges,
  userId,
  onRefresh,
  onDeleteDevice,
}: {
  device: Device;
  deviceTokens: DeviceDYNCode[];
  hideConnectionBadges: boolean;
  userId: string;
  onRefresh: () => void;
  onDeleteDevice: (deviceId: string) => Promise<void>;
}) {
  return (
    <Group justify='space-between' align='center'>
      <Group gap='sm'>
        <ThemeIcon size='lg' variant='light' color='blue' w={32} h={32}>
          <Glasses size={20} />
        </ThemeIcon>
        <Stack gap={2}>
          <Group gap='sm' align='center'>
            <Text size='sm' fw={500}>
              {device.name || device.model || `Visore ${device.id}`}
            </Text>
            {!hideConnectionBadges && (
              <ConnectionBadge status={device.status} />
            )}
          </Group>
          <Text size='xs' c='dimmed'>
            ID: {device.id} | Tipo: {device.type} | Codice: {device.code}
          </Text>
          {device.model && (
            <Text size='xs' c='blue' fw={500}>
              Modello: {device.model}
            </Text>
          )}
          {device.created_at && (
            <Text size='xs' c='dimmed'>
              Aggiunto:{' '}
              {format(new Date(device.created_at), "dd/MM/yyyy 'alle' HH:mm", {
                locale: it,
              })}
            </Text>
          )}
        </Stack>
      </Group>
      <DeviceActionButtonsGroup
        device={device}
        deviceTokens={deviceTokens}
        userId={userId}
        onRefresh={onRefresh}
        onDeleteDevice={onDeleteDevice}
      />
    </Group>
  );
});

// Connected Sensors Section Component
const ConnectedSensorsSection = memo(function ConnectedSensorsSection({
  sensors,
  deviceTokens,
  hideConnectionBadges,
  userId,
  onConnect,
  onUnpair,
  onRefresh,
  onDeleteDevice,
}: {
  sensors: Device[];
  deviceTokens: DeviceDYNCode[];
  hideConnectionBadges: boolean;
  userId: string;
  onConnect: (device: Device) => void;
  onUnpair: (deviceId: string) => Promise<void>;
  onRefresh: () => void;
  onDeleteDevice: (deviceId: string) => Promise<void>;
}) {
  return (
    <>
      <Divider />
      <Stack gap='xs'>
        <Text size='xs' fw={500} c='dimmed' tt='uppercase'>
          Sensori Connessi ({sensors.length})
        </Text>
        {sensors.map(sensor => (
          <Card key={sensor.id} withBorder p='xs' bg='gray.0'>
            <Group justify='space-between' align='center'>
              <Group gap='sm'>
                <ThemeIcon
                  size='sm'
                  variant='light'
                  color='green'
                  w={32}
                  h={32}
                >
                  <Watch size={24} />
                </ThemeIcon>
                <Stack gap={1}>
                  <Group gap='xs' align='center'>
                    <Text size='xs' fw={500}>
                      {sensor.name || sensor.model || `Sensore ${sensor.id}`}
                    </Text>
                    {!hideConnectionBadges && (
                      <ConnectionBadge status={sensor.status} />
                    )}
                  </Group>
                  <Text size='xs' c='dimmed'>
                    ID: {sensor.id} | Codice: {sensor.code}
                  </Text>
                  {sensor.model && (
                    <Text size='xs' c='green' fw={500}>
                      Modello: {sensor.model}
                    </Text>
                  )}
                  {sensor.created_at && (
                    <Text size='xs' c='dimmed'>
                      Creato:{' '}
                      {format(
                        new Date(sensor.created_at),
                        "dd/MM/yyyy 'alle' HH:mm",
                        { locale: it }
                      )}
                    </Text>
                  )}
                </Stack>
              </Group>
              <Group gap='xs'>
                <DevicePairingActionsButtonGroup
                  device={sensor}
                  isPaired={!!sensor.connected_to}
                  onPair={onConnect}
                  onUnpair={onUnpair}
                  onSuccess={onRefresh}
                  showPairButton={false}
                  showUnpairButton={true}
                />
                <DeviceActionButtonsGroup
                  device={sensor}
                  deviceTokens={deviceTokens.filter(
                    token =>
                      token.deviceId === sensor.id.toString() ||
                      token.deviceCode === sensor.code ||
                      token.deviceCode === sensor.clientId
                  )}
                  userId={userId}
                  onRefresh={onRefresh}
                  onDeleteDevice={onDeleteDevice}
                />
              </Group>
            </Group>
          </Card>
        ))}
      </Stack>
    </>
  );
});

// Standalone Sensor Section Component
const StandaloneSensorSection = memo(function StandaloneSensorSection({
  sensor,
  hideConnectionBadges,
  onRefresh,
  onConnect,
  onUnpair,
  onDeleteDevice,
  canPair,
}: {
  sensor: Device;
  hideConnectionBadges: boolean;
  onRefresh: () => void;
  onConnect: (device: Device) => void;
  onUnpair: (deviceId: string) => Promise<void>;
  onDeleteDevice: (deviceId: string) => Promise<void>;
  canPair: boolean;
}) {
  return (
    <Group justify='space-between' align='center'>
      <Group gap='sm'>
        <ThemeIcon size='lg' variant='light' color='green'>
          <Watch size={20} />
        </ThemeIcon>
        <Stack gap={2}>
          <Group gap='sm' align='center'>
            <Text size='sm' fw={500}>
              {sensor.name || sensor.model || `Smartwatch ${sensor.id}`}
            </Text>
            {!hideConnectionBadges && (
              <ConnectionBadge status={sensor.status} />
            )}
          </Group>
          <Text size='xs' c='dimmed'>
            ID: {sensor.id} | Tipo: {sensor.type} | Codice: {sensor.code}
          </Text>
          {sensor.model && (
            <Text size='xs' c='green' fw={500}>
              Modello: {sensor.model}
            </Text>
          )}
          {sensor.created_at && (
            <Text size='xs' c='dimmed'>
              Aggiunto:{' '}
              {format(new Date(sensor.created_at), "dd/MM/yyyy 'alle' HH:mm", {
                locale: it,
              })}
            </Text>
          )}
        </Stack>
      </Group>
      <Group gap='xs'>
        <DevicePairingActionsButtonGroup
          device={sensor}
          isPaired={!!sensor.connected_to}
          onPair={onConnect}
          onUnpair={onUnpair}
          onSuccess={onRefresh}
          pairDisabled={!canPair}
        />
        <DeleteDeviceButton
          deviceId={sensor.id.toString()}
          deviceName={sensor.name || sensor.model}
          onDelete={onDeleteDevice}
          onSuccess={onRefresh}
        />
      </Group>
    </Group>
  );
});

// Device Card Component
const DeviceCard = memo(function DeviceCard({
  device,
  sensors,
  deviceTokens,
  hideConnectionBadges,
  userId,
  onConnect,
  onUnpair,
  onRefresh,
  onDeleteDevice,
}: {
  device: Device;
  sensors: Device[];
  deviceTokens: DeviceDYNCode[];
  hideConnectionBadges: boolean;
  userId: string;
  onConnect: (device: Device) => void;
  onUnpair: (deviceId: string) => Promise<void>;
  onRefresh: () => void;
  onDeleteDevice: (deviceId: string) => Promise<void>;
}) {
  const tokensForDevice = useMemo(
    () =>
      deviceTokens.filter(
        token =>
          token.deviceId === device.id.toString() ||
          token.deviceCode === device.code ||
          token.deviceCode === device.clientId
      ),
    [deviceTokens, device]
  );

  return (
    <Card withBorder p='md'>
      <Stack gap='md'>
        <VRHeadsetSection
          device={device}
          deviceTokens={tokensForDevice}
          hideConnectionBadges={hideConnectionBadges}
          userId={userId}
          onRefresh={onRefresh}
          onDeleteDevice={onDeleteDevice}
        />

        {sensors.length > 0 && (
          <ConnectedSensorsSection
            sensors={sensors}
            deviceTokens={deviceTokens}
            hideConnectionBadges={hideConnectionBadges}
            userId={userId}
            onConnect={onConnect}
            onUnpair={onUnpair}
            onRefresh={onRefresh}
            onDeleteDevice={onDeleteDevice}
          />
        )}
      </Stack>
    </Card>
  );
});

// Main DeviceList Component
export const DeviceList = memo<DeviceListProps>(
  ({
    userId,
    devices,
    deviceTokens,
    isLoading,
    error,
    hideConnectionBadges = false,
    hideCreateButtons = false,
    onDeviceCreated,
    onRefresh,
    onDeleteDevice,
  }) => {
    // Modal states
    const [createType, setCreateType] = useState<
      'vr-headset' | 'smartwatch' | null
    >(null);
    const [selectedDevice, setSelectedDevice] = useState<Device | undefined>();

    const [createOpened, { open: openCreate, close: closeCreate }] =
      useDisclosure(false);
    const [connectOpened, { open: openConnect, close: closeConnect }] =
      useDisclosure(false);

    // Event handlers
    const handleCreateVR = () => {
      setCreateType('vr-headset');
      openCreate();
    };

    const handleCreateSensor = () => {
      setCreateType('smartwatch');
      openCreate();
    };

    const handleConnect = (device: Device) => {
      setSelectedDevice(device);
      openConnect();
    };

    const handleUnpair = async (deviceId: string) => {
      if (!userId) {
        throw new Error('User ID is required to unpair device');
      }
      await unpairDeviceSafe(userId, deviceId);
      onRefresh();
    };

    const handleCreateCallback = () => {
      closeCreate();
      setCreateType(null);
      onRefresh();
      onDeviceCreated?.();
    };

    const handleConnectCallback = () => {
      closeConnect();
      setSelectedDevice(undefined);
      onRefresh();
    };

    if (isLoading) {
      return (
        <Card withBorder p='md'>
          <Flex justify='center' align='center' h={200}>
            <Stack align='center' gap='md'>
              <Loader size='lg' />
              <Text>Caricamento dispositivi...</Text>
            </Stack>
          </Flex>
        </Card>
      );
    }

    if (error) {
      return (
        <Card withBorder p='md'>
          <Text c='red'>Errore nel caricamento dei dispositivi: {error}</Text>
        </Card>
      );
    }

    // Filter devices by type
    const smartwatches = (devices || []).filter(d => d.type === 'smartwatch');
    const vrHeadsets = (devices || []).filter(d => d.type === 'vr-headset');
    const standaloneSmartwatch = smartwatches.filter(d => !d.connected_to);
    const canPair = canPairSensor(devices || []);

    return (
      <>
        <Card withBorder p='md'>
          <Stack gap='md'>
            <DevicesHeader
              onCreateVR={handleCreateVR}
              onCreateSensor={handleCreateSensor}
              hideCreateButtons={hideCreateButtons}
            />

            {devices && devices.length > 0 ? (
              <Stack gap='sm'>
                <Text size='xs' c='dimmed'>
                  Totale dispositivi: {devices.length}
                </Text>
                {/* VR Headsets */}
                {vrHeadsets.map(device => (
                  <DeviceCard
                    key={device.id}
                    device={device}
                    sensors={smartwatches.filter(sensor => {
                      const matches =
                        sensor.connected_to === device.id ||
                        sensor.connected_to?.toString() ===
                          device.id.toString();
                      return matches;
                    })}
                    deviceTokens={deviceTokens}
                    hideConnectionBadges={hideConnectionBadges}
                    userId={userId}
                    onConnect={handleConnect}
                    onUnpair={handleUnpair}
                    onRefresh={onRefresh}
                    onDeleteDevice={onDeleteDevice}
                  />
                ))}

                {/* Standalone Sensors */}
                {standaloneSmartwatch.length > 0 && (
                  <>
                    <Divider />
                    <Stack gap='sm'>
                      <Title order={4}>Sensori non collegati</Title>
                      {standaloneSmartwatch.map(sensor => (
                        <Card key={sensor.id} withBorder p='md'>
                          <StandaloneSensorSection
                            sensor={sensor}
                            hideConnectionBadges={hideConnectionBadges}
                            onRefresh={onRefresh}
                            onConnect={handleConnect}
                            onUnpair={handleUnpair}
                            onDeleteDevice={onDeleteDevice}
                            canPair={canPair}
                          />
                        </Card>
                      ))}
                    </Stack>
                  </>
                )}
              </Stack>
            ) : (
              <Text c='dimmed'>
                Nessun dispositivo trovato per questo utente.
              </Text>
            )}
          </Stack>
        </Card>
        {/* Create Device (VR Headsets and Sensors) Modal */}
        <CreateDeviceModal
          opened={createOpened}
          close={() => {
            closeCreate();
            setCreateType(null);
          }}
          callback={handleCreateCallback}
          defaultType={createType}
          userId={userId}
        />
        {/* Connect Device (Sensors) Modal */}
        {selectedDevice && (
          <ConnectDeviceModal
            device={selectedDevice}
            opened={connectOpened}
            devices={devices || []}
            close={closeConnect}
            callback={handleConnectCallback}
            userId={userId}
            onRefresh={onRefresh}
          />
        )}
      </>
    );
  }
);

DeviceList.displayName = 'DeviceList';
